// =============================================================================
// domain/backtests.ts — the manual back-testing tracker's arithmetic.
// -----------------------------------------------------------------------------
// PURE. Ported near-verbatim from the old client's `backtestDerived`,
// `backtestGroupStats` and `backtestLiveStats` (app/index.html). The whole point
// of this tab is a side-by-side of a pattern's HAND-TESTED samples against the
// same pattern's REAL trades, so the two columns MUST be computed by definitions
// that match every other analytics surface — and match each other where they can.
//
// ── 🚨 TWO WIN-RATE DEFINITIONS, ON PURPOSE ──
//
// `backtestGroupStats` (the sample side) calls a trade a win when its POINTS are
// positive — a backtest carries no fees, so points is all there is. The live side
// (`backtestLiveStats`) calls it a win when NET P&L is positive, exactly like
// `patternLeaderboard` and every other live win-rate the trader sees, because on
// a real fill a fee can flip a hairline scratch and hiding that would be dishonest
// about the live column. The old client's comment is explicit that this asymmetry
// is deliberate; porting one definition to "tidy" it would silently move a number.
//
// ── ⚠ THE `Number(null) === 0` QUIRK IS PORTED, NOT FIXED ──
//
// `avgMae`/`avgMfe` map each record through `Math.abs(Number(x))` and keep every
// finite result — and `Number(null)` is `0`, which is finite, so a record with no
// MAE contributes a ZERO to the average rather than being skipped. That is what
// the old client does; the E-ratio it feeds only renders at n≥5 with avgMae above
// the floor, so on a thin sample it is moot — but the live column is computed the
// same way, and matching the trader's real E-ratio means matching the coercion.
// =============================================================================

import { ERATIO_MAE_FLOOR, ERATIO_MIN_SAMPLE } from './excursionEdge';

export type BacktestOutcome = 'win' | 'loss' | 'scratch';

/** The fields `backtestDerived` reads off one sample. */
export interface DerivableBacktest {
  side?: string | null;
  entry?: number | null;
  exit?: number | null;
  riskPoints?: number | null;
  mfePoints?: number | null;
}

/** …plus MAE, for the group aggregate. */
export interface GroupableBacktest extends DerivableBacktest {
  maePoints?: number | null;
}

export interface BacktestDerived {
  /** Signed points, long = exit−entry, short = entry−exit. `null` until complete. */
  points: number | null;
  /** points ÷ stop distance. `null` without a positive stop. */
  r: number | null;
  /** realized ÷ MFE, as a percentage. `null` without a positive MFE. */
  mfeCapture: number | null;
  outcome: BacktestOutcome | null;
}

/** One completed round-trip's points, R, MFE-capture and outcome. */
export function backtestDerived(b: DerivableBacktest): BacktestDerived {
  const entry = Number(b.entry);
  const exit = Number(b.exit);
  const hasPx = Number.isFinite(entry) && entry > 0 && Number.isFinite(exit) && exit > 0
    && (b.side === 'long' || b.side === 'short');
  const points = hasPx ? (b.side === 'long' ? exit - entry : entry - exit) : null;

  const risk = Number(b.riskPoints);
  const r = (points != null && Number.isFinite(risk) && risk > 0) ? points / risk : null;

  // ⚠ `Number(null)` is 0 → abs 0 → the `> 0` guard rejects it, so a missing MFE
  // yields a null capture rather than a division. Matches the old client.
  const mfe = Math.abs(Number(b.mfePoints));
  const mfeCapture = (points != null && Number.isFinite(mfe) && mfe > 0) ? (points / mfe) * 100 : null;

  const outcome: BacktestOutcome | null =
    points == null ? null : (points > 0 ? 'win' : (points < 0 ? 'loss' : 'scratch'));

  return { points, r, mfeCapture, outcome };
}

/** The five-column comparison row — same shape for the sample and live sides. */
export interface BacktestStats {
  /** Records in the group (sample side) or metric-eligible trades (live side). */
  n: number;
  wins: number;
  losses: number;
  /** wins ÷ (wins+losses) × 100, scratches excluded. `null` with no decided trade. */
  winRate: number | null;
  avgR: number | null;
  /** Records that carried a stop, i.e. the denominator behind `avgR`. */
  nR: number;
  avgMae: number | null;
  avgMfe: number | null;
  /** avgMFE ÷ avgMAE, gated by sample size and the MAE floor. `null` otherwise. */
  eRatio: number | null;
  /** MEDIAN MFE-capture across decided trades. `null` when none has an MFE. */
  medCapture: number | null;
}

function mean(xs: readonly number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

function median(xs: readonly number[]): number | null {
  if (!xs.length) return null;
  const s = xs.slice().sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/**
 * The one place the five columns are assembled, so the sample side and the live
 * side cannot drift in how they gate E-ratio or take the capture median. Callers
 * differ only in how they DECIDE wins/losses and R — see the two win-rate note.
 */
function combine(
  n: number, wins: number, losses: number,
  rs: readonly number[], maes: readonly number[], mfes: readonly number[],
  captures: readonly number[],
): BacktestStats {
  const avgMae = mean(maes);
  const avgMfe = mean(mfes);
  const eRatio = (
    Math.min(maes.length, mfes.length) >= ERATIO_MIN_SAMPLE
    && avgMae != null && avgMae > ERATIO_MAE_FLOOR
    && avgMfe != null
  ) ? avgMfe / avgMae : null;
  return {
    n, wins, losses,
    winRate: (wins + losses) ? (wins / (wins + losses)) * 100 : null,
    avgR: mean(rs), nR: rs.length,
    avgMae, avgMfe, eRatio,
    medCapture: median(captures),
  };
}

/** Aggregate over a pattern's hand-recorded samples. Win = positive POINTS. */
export function backtestGroupStats(records: readonly GroupableBacktest[]): BacktestStats {
  const ds = records.map(backtestDerived);
  const done = ds.filter((d) => d.outcome !== null);
  const wins = done.filter((d) => (d.points ?? 0) > 0).length;
  const losses = done.filter((d) => (d.points ?? 0) < 0).length;
  const rs = done.map((d) => d.r).filter((r): r is number => r != null && Number.isFinite(r));
  // ⚠ `Number(null) === 0` is kept — see the header note.
  const maes = records.map((b) => Math.abs(Number(b.maePoints))).filter((v) => Number.isFinite(v));
  const mfes = records.map((b) => Math.abs(Number(b.mfePoints))).filter((v) => Number.isFinite(v));
  const captures = done.map((d) => d.mfeCapture).filter((v): v is number => v != null && Number.isFinite(v));
  return combine(records.length, wins, losses, rs, maes, mfes, captures);
}

/** What the live comparison reads off one real trade. */
export interface LiveTradeStat {
  netPnl?: number | null;
  rrRealized?: number | null;
  maePoints?: number | null;
  mfePoints?: number | null;
  points?: number | null;
}

/**
 * The live side of the comparison. Win = positive NET P&L (matches every other
 * live win-rate); R from stored realized R; capture from realized points ÷ MFE.
 *
 * ⚠ The caller pre-filters to metric-eligible trades of this pattern, so `n` is
 * that count — and it stays pure and testable by taking the trades, not the id.
 */
export function backtestLiveStats(trades: readonly LiveTradeStat[]): BacktestStats {
  const pnls = trades.map((t) => Number(t.netPnl)).filter((v) => Number.isFinite(v));
  const wins = pnls.filter((v) => v > 0).length;
  const losses = pnls.filter((v) => v < 0).length;
  const rs = trades.map((t) => Number(t.rrRealized)).filter((v) => Number.isFinite(v));
  const maes = trades.map((t) => Math.abs(Number(t.maePoints))).filter((v) => Number.isFinite(v));
  const mfes = trades.map((t) => Math.abs(Number(t.mfePoints))).filter((v) => Number.isFinite(v));
  const captures = trades.map((t) => {
    const pts = Number(t.points);
    const mfe = Math.abs(Number(t.mfePoints));
    return (Number.isFinite(pts) && Number.isFinite(mfe) && mfe > 0) ? (pts / mfe) * 100 : null;
  }).filter((v): v is number => v != null);
  return combine(trades.length, wins, losses, rs, maes, mfes, captures);
}
